import { useCallback, useMemo, useState } from 'react';
import type { AccessMap, MapEdge, MapNode, KindIn } from './AccessMap.api';

/**
 * Layout do mapa de acesso (T19, FR-102).
 *
 * Três colunas — Human Access (só sob a lente) · Identidades · Resources — e,
 * dentro de cada uma, ordenação por **baricentro**: cada nó fica na altura média
 * dos vizinhos da coluna anterior, que é o que reduz o cruzamento de arestas sem
 * precisar de biblioteca de grafo.
 *
 * A COLUNA «POLÍTICAS EXTRAS» SAIU (04/09), e com ela o modo que a colapsava.
 * Ela desenhava `identidade → política → resource`, ou seja, «a política concede
 * à identidade» — a semântica de união. O servidor não emite mais nó de política
 * (`kind: 'politica'`), então não há o que colocar em coluna nem o que colapsar.
 *
 * Posições arrastadas ficam em `localStorage` (`aim.accessmap.positions`), com
 * fallback silencioso: navegador sem storage (aba anônima, política de site)
 * apenas perde a memória, nunca quebra a tela.
 */

/**
 * Geometria do protótipo (`rAccessMap`): o nó é um **disco** de raio 26 com o
 * ícone do tipo dentro e os rótulos por baixo — não uma caixa. `x`/`y` de
 * `NoPosicionado` são o **centro** do disco: é o que as arestas ligam
 * (`x ± NODE_R`) e o que o arrasto grava.
 */
export const NODE_R = 26;
/** Anel que recorta o disco do fundo (`.noDisco`, `stroke-width: 3`). */
export const NODE_ANEL = 1.5;
/** Linha de base dos dois rótulos do nó, medida do centro do disco. */
export const LABEL_DY = NODE_R + 18;
export const SUB_DY = NODE_R + 32;
/** Corte dos dois rótulos do nó (`truncar`). */
export const LABEL_LIMIT = 26;
/**
 * Geometria da aresta entre nós da **mesma coluna** (A2A entre identidades):
 * abaixo de `MESMA_COLUNA` de diferença em `x` ela vira um laço que sai
 * `RECUO_DO_LACO` à esquerda, senão o traço seria uma reta vertical em cima da
 * própria coluna. Ficam aqui, e não em `Graph.tsx`, porque o enquadramento
 * (`caberNaArea`) precisa saber até onde o laço vai para não cortá-lo.
 */
export const SAME_COLUMN = 60;
export const RECUO_DO_LACO = 120;
/** Colunas em 90 · 350 · 610 (protótipo: 90 · 340 · 610). */
const COL_X0 = 90;
const COL_GAP = 260;
/** Faixa do rótulo da coluna, antes da primeira linha. */
const TOP = 70;
const ROW_H = 88;
/** Do topo da faixa até o centro do primeiro disco. */
const ROW_OFFSET = 30;
/** Folga à direita e embaixo: o rótulo é centrado no disco e transborda. */
const PAD_RIGHT = 200;
/** Exportada porque o esticamento vertical recalcula a moldura com ela. */
export const PAD_BOTTOM = 56;

export const STORAGE_KEY = 'aim.accessmap.positions';

/**
 * Ordem das colunas. `politica` NÃO está aqui desde 04/09 — o servidor não emite
 * mais esse nó, e uma coluna que nunca recebe nó não é uma coluna.
 */
const COLUMNS: KindIn[] = ['human_subject', 'identidade', 'resource'];

/**
 * Coluna → chave do catálogo. Guarda a CHAVE e não a frase: o valor à esquerda
 * vem do contrato (`KindIn`) e não se traduz; o rótulo, sim, e é resolvido no
 * desenho para o seletor de idioma valer.
 */
export const COLUMN_LABEL_KEY: Record<KindIn, string> = {
  // Desde a Fase 3 esta coluna só existe sob a lente «Ver como».
  human_subject: 'accessMap.column.humanAccess',
  identidade: 'accessMap.column.identities',
  // Sem uso: `politica` saiu de `COLUMNS`. A chave fica porque `KindIn` ainda
  // declara o valor (enum do contrato).
  politica: 'accessMap.column.rules',
  resource: 'accessMap.column.resources',
};

export interface Point {
  x: number;
  y: number;
}

export interface NoPosicionado extends Point {
  no: MapNode;
  coluna: number;
}

export interface EdgePosicionada {
  chave: string;
  aresta: MapEdge;
  de: NoPosicionado;
  para: NoPosicionado;
}

export interface GraphLayout {
  nos: NoPosicionado[];
  arestas: EdgePosicionada[];
  colunas: { tipo: KindIn; rotuloKey: string; x: number }[];
  largura: number;
  altura: number;
  /** Vizinhos imediatos de um nó, nos dois sentidos. */
  vizinhanca: (id: string) => Set<string>;
}

type Posicoes = Record<string, Point>;

function readPosicoes(tenant: string): Posicoes {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const all = JSON.parse(raw) as Record<string, Posicoes>;
    return all[tenant] ?? {};
  } catch {
    // Storage indisponível ou conteúdo corrompido: o mapa abre no automático.
    return {};
  }
}

function storePosicoes(tenant: string, posicoes: Posicoes): void {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const all = raw ? (JSON.parse(raw) as Record<string, Posicoes>) : {};
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...all, [tenant]: posicoes }));
  } catch {
    // Não poder lembrar a posição não é motivo para perder o arrasto na tela.
  }
}

/** Ordena a coluna pela altura média dos vizinhos da coluna anterior. */
function byBaricentro(coluna: MapNode[], previousIndex: Map<string, number>, arestas: MapEdge[]) {
  if (previousIndex.size === 0) return coluna;
  const peso = new Map<string, number>();
  coluna.forEach((no, index) => {
    const vizinhos = arestas
      .filter((a) => a.to === no.id && previousIndex.has(a.from))
      .map((a) => previousIndex.get(a.from) as number);
    // Sem vizinho na coluna anterior, o nó mantém a posição relativa de entrada.
    peso.set(
      no.id,
      vizinhos.length ? vizinhos.reduce((s, v) => s + v, 0) / vizinhos.length : index,
    );
  });
  return [...coluna].sort((a, b) => (peso.get(a.id) ?? 0) - (peso.get(b.id) ?? 0));
}

export interface UseGraphLayoutOptions {
  /**
   * Chave do tenant no storage — `tenant.cliente` da sessão. A PoC é
   * single-tenant, mas o mesmo navegador atende mais de um cliente na demo, e o
   * layout arrastado num não pode reaparecer no outro.
   */
  tenant?: string;
}

export function useGraphLayout(
  mapping: AccessMap | undefined,
  { tenant = 'default' }: UseGraphLayoutOptions,
) {
  const [estado, setEstado] = useState<{ tenant: string; posicoes: Posicoes }>(() => ({
    tenant,
    posicoes: readPosicoes(tenant),
  }));

  // O tenant só se conhece depois do `getMe`. Quando ele troca, relê o balde do
  // cliente novo em vez de herdar as posições de quem estava antes na tela.
  // Atualizar estado **durante o render** do próprio componente é o caminho que
  // o React documenta para isso: ele descarta esta passagem e reexecuta com o
  // estado novo, então o que se desenha já é do tenant certo.
  if (estado.tenant !== tenant) setEstado({ tenant, posicoes: readPosicoes(tenant) });
  const { posicoes } = estado;

  const mover = useCallback(
    (id: string, ponto: Point) => {
      const next = { ...posicoes, [id]: ponto };
      storePosicoes(tenant, next);
      setEstado({ tenant, posicoes: next });
    },
    [posicoes, tenant],
  );

  /** **Reorganizar**: descarta o que foi arrastado e volta ao automático. */
  const reorganizar = useCallback(() => {
    storePosicoes(tenant, {});
    setEstado({ tenant, posicoes: {} });
  }, [tenant]);

  const layout = useMemo<GraphLayout>(() => {
    const empty: GraphLayout = {
      nos: [],
      arestas: [],
      colunas: [],
      largura: 0,
      altura: 0,
      vizinhanca: () => new Set<string>(),
    };
    if (!mapping) return empty;

    const { nodes: nos, edges: arestas } = mapping;
    if (nos.length === 0) return empty;

    const kinds = COLUMNS.filter((tipo) => nos.some((no) => no.kind === tipo));

    const posicionados: NoPosicionado[] = [];
    let previousIndex = new Map<string, number>();

    // Cada coluna é **centrada** na altura da mais cheia (protótipo: `offset =
    // (maxRows - total) * rowH / 2`). Sem isso as colunas curtas ficam
    // penduradas no topo e o desenho descola do meio do palco.
    const rowsByKind = kinds.map((tipo) => nos.filter((no) => no.kind === tipo).length);
    const maxRows = Math.max(1, ...rowsByKind);

    kinds.forEach((tipo, coluna) => {
      const column = byBaricentro(
        nos.filter((no) => no.kind === tipo),
        previousIndex,
        arestas,
      );
      const centragem = ((maxRows - column.length) * ROW_H) / 2;
      const nextIndex = new Map<string, number>();
      column.forEach((no, linha) => {
        const saved = posicoes[no.id];
        posicionados.push({
          no,
          coluna,
          x: saved?.x ?? COL_X0 + coluna * COL_GAP,
          y: saved?.y ?? TOP + centragem + linha * ROW_H + ROW_OFFSET,
        });
        nextIndex.set(no.id, linha);
      });
      previousIndex = nextIndex;
    });

    const byID = new Map(posicionados.map((item) => [item.no.id, item]));

    const edgesPosicionadas: EdgePosicionada[] = arestas
      .map((edge, index) => {
        const de = byID.get(edge.from);
        const para = byID.get(edge.to);
        if (!de || !para) return null;
        return { chave: `${edge.from}->${edge.to}-${edge.kind}-${index}`, aresta: edge, de, para };
      })
      .filter((item): item is EdgePosicionada => item !== null);

    const vizinhos = new Map<string, Set<string>>();
    for (const { aresta } of edgesPosicionadas) {
      if (!vizinhos.has(aresta.from)) vizinhos.set(aresta.from, new Set());
      if (!vizinhos.has(aresta.to)) vizinhos.set(aresta.to, new Set());
      vizinhos.get(aresta.from)?.add(aresta.to);
      vizinhos.get(aresta.to)?.add(aresta.from);
    }

    return {
      nos: posicionados,
      arestas: edgesPosicionadas,
      colunas: kinds.map((tipo, coluna) => ({
        tipo,
        rotuloKey: COLUMN_LABEL_KEY[tipo],
        x: COL_X0 + coluna * COL_GAP,
      })),
      largura: Math.max(...posicionados.map((item) => item.x)) + PAD_RIGHT,
      // O `max` com o nó mais baixo é por causa do arrasto: posição salva pode
      // cair fora da grade, e a moldura tem de continuar contendo o desenho.
      altura: Math.max(
        TOP + maxRows * ROW_H + PAD_BOTTOM,
        Math.max(...posicionados.map((item) => item.y)) + PAD_BOTTOM,
      ),
      vizinhanca: (id: string) => vizinhos.get(id) ?? new Set<string>(),
    };
  }, [mapping, posicoes]);

  return {
    layout,
    mover,
    reorganizar,
    /** Há nó arrastado? É o que faz **Reorganizar** aparecer. */
    arrastado: Object.keys(posicoes).length > 0,
  };
}
